// Privacy.js — /privacy static privacy policy.
//
// Plain-text policy covering what OrangeLabel actually collects: optional
// submitter email (internal, 14-day TTL), public business records,
// IP-derived geo inference, first-party + Umami analytics pageviews. No
// tracking cookies, no third-party ad networks, no selling of data.

import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageMeta from '@/components/PageMeta';

const LAST_UPDATED = '2026-04-18';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <PageMeta title="Privacy policy" path="/privacy" description="What OrangeLabel collects, retains, and shares. Minimal data, clear retention windows, no third-party ad networks." />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-foreground mb-2" data-testid="privacy-heading">
          Privacy policy
        </h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-sm sm:prose-base max-w-none text-foreground space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold mb-2">Short version</h2>
            <p>
              OrangeLabel is a public directory of Bitcoin-accepting merchants
              and events. We collect the minimum needed to run it: admin
              sessions, submissions, and anonymous page-view counts. No
              third-party ad networks, no cross-site tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Information we collect</h2>
            <h3 className="font-semibold mt-3">Public business records</h3>
            <p>
              Everything shown on the public directory, map, and event pages
              is intentionally public: business name, website, contact
              channel, category, payment methods, physical address (for
              physical merchants), and service scope.
            </p>
            <h3 className="font-semibold mt-3">Submissions</h3>
            <p>
              When you submit a business or meetup via <Link to="/submit" className="text-primary hover:underline">/submit</Link>,
              the business details are stored publicly. Providing a submitter
              contact email is <strong>optional</strong> — the field is clearly
              marked "(optional)" and the form works without one. If you do
              provide one, it's stored internally only so we can reach you if
              the submission needs clarification; it is <strong>never</strong> published
              and <strong>never</strong> written to the merchant record's public
              <code className="text-xs bg-muted px-1 rounded">email</code> field
              or pushed downstream to OpenStreetMap / BTCMap.
              {' '}<strong>Submitter contact (email and name) is auto-deleted
              after 14 days</strong> by a daily retention sweep — the business
              record stays, only your contact info goes.
            </p>
            <h3 className="font-semibold mt-3">Geolocation</h3>
            <p>
              To show you merchants near you on first load, we read the
              country code Cloudflare derives from your IP at its edge, plus
              your browser's timezone. Neither your raw IP nor your precise
              coordinates touch our storage — we only see the country code.
              To opt out, use a VPN or block the geo request in your
              browser; the app falls back to the global directory.
            </p>
            <h3 className="font-semibold mt-3">Analytics</h3>
            <p>
              We run two self-hosted, cookieless analytics pipelines — no
              third-party analytics SaaS, no ad-tech SDKs, no session replay,
              no cross-site identifiers.
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>
                <strong>First-party pageviews</strong> — on each navigation
                we record: path, referrer, page title, and a coarse device
                class (mobile / tablet / desktop). To count unique visitors
                we derive an anonymous short ID by hashing your IP + User-Agent;
                the raw IP is never written to the database.
              </li>
              <li>
                <strong>Umami</strong> — a self-hosted, open-source analytics
                tool (<a href="https://umami.is" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">umami.is</a>)
                we run on our own infrastructure. Umami is cookieless, sets
                no persistent identifiers, and does not share data with any
                third party. No data leaves our servers.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">How we use it</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Run the directory + map + events pages you came for.</li>
              <li>Let admins moderate submissions and content.</li>
              <li>Aggregate counts for public stats on the homepage.</li>
              <li>Reply to you if you contacted us or submitted a business that needs follow-up.</li>
            </ul>
            <p className="mt-3">
              That's it. We do not profile users, target advertising, score
              credit, build shadow profiles, or train machine-learning
              models on any visitor data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">What we share</h2>
            <p>
              Public directory content is shared with two third parties by
              design:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>OpenStreetMap</strong> — submissions for physical
                businesses with coordinates are written to OSM via our
                OAuth-authenticated OrangeLabel account. Your submitter
                email is stripped before submission.
              </li>
              <li>
                <strong>BTCMap.org</strong> — after an OSM submission,
                records are automatically flipped to be sourced from
                BTCMap's open data feed for long-term curation.
              </li>
            </ul>
            <p>
              We do not share admin session tokens, submitter emails,
              analytics data (first-party or Umami), or IP-derived country
              codes with any third party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Cookies &amp; storage</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <code className="text-xs bg-muted px-1 rounded">session_token</code> —
                httpOnly admin session cookie. Set only after admin login.
                7-day expiry. Not set for anonymous visitors.
              </li>
              <li>
                <code className="text-xs bg-muted px-1 rounded">sessionStorage</code> —
                browser-local only (never sent to the server): a short cache of
                the resolved country/region so we don't re-infer on every page
                navigation within one tab.
              </li>
            </ul>
            <p>No third-party cookies. No advertising identifiers. No trackers.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Your rights</h2>
            <p>
              Because we hold so little personal data, your rights map to
              quick requests:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <strong>Remove a business listing</strong> — submit a
                takedown request via <Link to="/contact" className="text-primary hover:underline">/contact</Link>
                {' '}including the business URL. Owner-verified takedowns are
                processed within 7 days.
              </li>
              <li>
                <strong>Correct a business listing</strong> — use the
                "Suggest an edit" button on the listing's popup, or email us.
              </li>
              <li>
                <strong>Remove submitter email early</strong> — submitter
                contact is auto-deleted after 14 days, but if you want it
                gone sooner just contact us.
              </li>
              <li>
                <strong>Opt out of geolocation</strong> — see the Geolocation
                section above; the app falls back to the global directory.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Data retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Public business records: kept indefinitely unless removed on request.</li>
              <li>Submitter contact (email + name): auto-deleted 14 days after submission by a daily retention sweep.</li>
              <li>Analytics page-view rows: retained for 90 days, then aggregated into non-identifiable monthly totals.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Security</h2>
            <p>
              Connections are TLS-encrypted end-to-end. Passwords are hashed
              before storage. Admin endpoints require authentication.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Contact</h2>
            <p>
              Questions, corrections, takedowns: <Link to="/contact" className="text-primary hover:underline">/contact</Link>.
              This site is maintained by the <a href="https://bitcoinindustrybody.org.au" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Australian Bitcoin Industry Body</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">Changes to this policy</h2>
            <p>
              When this policy changes we update the "Last updated" date at
              the top. For material changes (new data collection or new
              third-party sharing) we'll aim to flag it on the site so you
              have a chance to notice before continuing to use it.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
